import { locale } from '../lib/locale';

/**
 * "Was essen wir heute?" (Küche 4, 1.22): Essen → Heute, the Larovi-Ideenbuch, "Was ist da?",
 * "Überrasch uns", "Offene Tage vorschlagen". Texts approved with the plan (Mike, 17.10.).
 * Glossary: Ideenbuch = Larovi idea book, Vorschlag = suggestion. German is the master.
 */
export const IDEAS_TEXT = {
  de: {
    tab: 'Heute',
    title: 'Was essen wir heute?',
    dayBusyPerson: (name: string, until: string, what: string) =>
      `${name} hat bis ${until} ${what} – heute lieber etwas Schnelles.`,
    dayBusy: 'Heute ist viel los – lieber etwas Schnelles.',
    dayRelaxed: 'Heute habt ihr Zeit – wie wär’s mit etwas Neuem?',
    planned: (dish: string, time: string) => `Heute gibt’s ${dish} · ${time} Uhr`,
    cook: 'Kochen',
    other: 'Lieber etwas anderes',
    wishes: 'Worauf habt ihr Lust?',
    wish: {
      quick: 'Schnell',
      veggie: 'Vegetarisch',
      kids: 'Kinder mögen’s',
      surprise: 'Überrasch uns',
      fridge: 'Was ist da?',
    },
    suggestions: 'Vorschläge',
    from: { own: 'Aus euren Rezepten', idea: 'Aus dem Ideenbuch', ai: 'Neue Idee' },
    reason: {
      longAgo: (days: number) => `vor ${days} Tagen gekocht`,
      kids: 'Kinder mögen’s',
      minutes: (m: number) => `${m} Min.`,
      new: 'neu für euch',
      often: 'oft gekocht',
      fits: (items: string) => `passt zu: ${items}`,
      missing: (items: string) => `fehlt: ${items}`,
      allThere: 'alles da',
    },
    dislikedBy: (names: string) => `nicht für ${names}`,
    take: 'Das nehmen wir',
    taken: (dish: string) => `Eingetragen – heute gibt’s ${dish}.`,
    more: 'Andere Vorschläge',
    adopt: 'In unsere Rezepte',
    adopted: 'Steht jetzt in euren Rezepten.',
    look: 'Ansehen',
    fridgeLabel: 'Was ist da?',
    fridgeExample: 'z. B. Paprika, Feta, Kartoffeln',
    fridgeSearch: 'Passendes suchen',
    missingToList: 'Fehlendes auf die Liste',
    missingAdded: (n: number) =>
      n === 1 ? '1 Sache steht auf der Liste.' : `${n} Sachen stehen auf der Liste.`,
    aiIdea: 'Neue Idee mit KI',
    aiLoading: 'Die KI denkt sich etwas aus …',
    aiTitle: 'Neue Ideen',
    nothing: 'Dazu haben wir gerade nichts. Probiert „Überrasch uns“ oder einen anderen Wunsch.',
    plusTitle: 'Was essen wir heute?',
    plusText:
      'Mit Larovi Plus schlägt Larovi jeden Tag drei passende Gerichte vor – aus euren Rezepten und dem Larovi-Ideenbuch.',
    notPlannedTitle: 'Noch nichts geplant',
    notPlannedLink: 'Vorschläge ansehen',
    // Woche
    fillWeek: 'Offene Tage vorschlagen',
    fillTitle: 'Vorschläge für die offenen Tage',
    fillNone: 'Diese Woche ist schon alles geplant.',
    swap: 'Anderer Vorschlag',
    skip: 'Weglassen',
    skipped: 'weggelassen',
    fillAll: (n: number) => (n === 1 ? '1 Tag eintragen' : `Alle ${n} eintragen`),
    filled: (n: number) => (n === 1 ? '1 Tag ist eingetragen.' : `${n} Tage sind eingetragen.`),
    ideaSource: 'Aus dem Larovi-Ideenbuch',
    // Woche: a day of its own gets three suggestions too (Mike, 17.10.: "auf einen anderen Tag
    // geht und dann dort auch immer die 3 Vorschläge bekommen")
    dayBusyPersonOn: (name: string, until: string, what: string) =>
      `${name} hat bis ${until} ${what} – lieber etwas Schnelles.`,
    dayBusyOn: 'An dem Tag ist viel los – lieber etwas Schnelles.',
    dayRelaxedOn: 'Da habt ihr Zeit – wie wär’s mit etwas Neuem?',
    searchDish: 'Gericht suchen',
    searchExample: 'z. B. Lasagne oder Linsen',
    searchNone: 'Nichts gefunden – schreibt es unten einfach so hin.',
    // Rezepte: the Ideenbuch to browse (Mike, 17.10.: "wo ich die Übersicht finde?!")
    bookCount: (n: number) => `${n} Ideen aus dem Larovi-Ideenbuch`,
    // searching the own recipes: the Ideenbuch only when asked (Mike, 17.10.: "erweiterte Suche")
    bookHits: (n: number) =>
      n === 1 ? 'Auch im Ideenbuch: 1 Treffer' : `Auch im Ideenbuch: ${n} Treffer`,
    collection: 'Welche Rezepte?',
    ownTitle: 'Eure Rezepte',
    bookTitle: 'Larovi-Ideenbuch',
    photoBy: (name: string) => `Foto: ${name} / Unsplash`,
  },
  en: {
    tab: 'Today',
    title: 'What’s for dinner today?',
    dayBusyPerson: (name: string, until: string, what: string) =>
      `${name} has ${what} until ${until} – something quick today.`,
    dayBusy: 'A busy day – something quick today.',
    dayRelaxed: 'You have time today – how about something new?',
    planned: (dish: string, time: string) => `Today it’s ${dish} · ${time}`,
    cook: 'Cook',
    other: 'Rather something else',
    wishes: 'What do you fancy?',
    wish: {
      quick: 'Quick',
      veggie: 'Vegetarian',
      kids: 'Kids like it',
      surprise: 'Surprise us',
      fridge: 'What’s there?',
    },
    suggestions: 'Suggestions',
    from: { own: 'From your recipes', idea: 'From the idea book', ai: 'New idea' },
    reason: {
      longAgo: (days: number) => `cooked ${days} days ago`,
      kids: 'kids like it',
      minutes: (m: number) => `${m} min`,
      new: 'new for you',
      often: 'cooked often',
      fits: (items: string) => `uses: ${items}`,
      missing: (items: string) => `missing: ${items}`,
      allThere: 'all there',
    },
    dislikedBy: (names: string) => `not for ${names}`,
    take: 'We’ll have that',
    taken: (dish: string) => `Added – today it’s ${dish}.`,
    more: 'Other suggestions',
    adopt: 'Add to our recipes',
    adopted: 'It’s in your recipes now.',
    look: 'Look',
    fridgeLabel: 'What’s there?',
    fridgeExample: 'e.g. peppers, feta, potatoes',
    fridgeSearch: 'Find something',
    missingToList: 'Put what’s missing on the list',
    missingAdded: (n: number) =>
      n === 1 ? '1 thing is on the list.' : `${n} things are on the list.`,
    aiIdea: 'New idea with AI',
    aiLoading: 'The AI is thinking of something …',
    aiTitle: 'New ideas',
    nothing: 'Nothing for that right now. Try “Surprise us” or another wish.',
    plusTitle: 'What’s for dinner today?',
    plusText:
      'With Larovi Plus, Larovi suggests three dishes that fit your day – from your recipes and the Larovi idea book.',
    notPlannedTitle: 'Nothing planned yet',
    notPlannedLink: 'See suggestions',
    fillWeek: 'Suggest the open days',
    fillTitle: 'Suggestions for the open days',
    fillNone: 'This week is all planned.',
    swap: 'Another suggestion',
    skip: 'Leave out',
    skipped: 'left out',
    fillAll: (n: number) => (n === 1 ? 'Add 1 day' : `Add all ${n}`),
    filled: (n: number) => (n === 1 ? '1 day is added.' : `${n} days are added.`),
    ideaSource: 'From the Larovi idea book',
    dayBusyPersonOn: (name: string, until: string, what: string) =>
      `${name} has ${what} until ${until} – something quick.`,
    dayBusyOn: 'A busy day – something quick.',
    dayRelaxedOn: 'You have time that day – how about something new?',
    searchDish: 'Search for a dish',
    searchExample: 'e.g. lasagne or lentils',
    searchNone: 'Nothing found – simply write it below.',
    bookCount: (n: number) => `${n} ideas from the Larovi idea book`,
    bookHits: (n: number) =>
      n === 1 ? 'Also in the idea book: 1 match' : `Also in the idea book: ${n} matches`,
    collection: 'Which recipes?',
    ownTitle: 'Your recipes',
    bookTitle: 'Larovi idea book',
    photoBy: (name: string) => `Photo: ${name} / Unsplash`,
  },
} as const;

export const ideasText = IDEAS_TEXT[locale];
